import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import useRequest from "../../../components/hook/use-req";
import { CLIENT_ROLES, COUNTRY_STATES, INDUSTRIES } from "../sample-data";

// New Client Setup (Figma, reseller). Only the Basic Details step has a
// design so far — Licensing and Review and Invite are placeholders, and
// nothing is sent anywhere: there is no reseller API yet.

const STEPS = ["Basic Details", "Licensing", "Review and Invite"];

interface ClientForm {
  companyName: string;
  workspace: string;
  industry: string;
  country: string;
  state: string;
  adminName: string;
  email: string;
  role: string;
  phone: string;
}

const EMPTY_FORM: ClientForm = {
  companyName: "",
  workspace: "",
  industry: "",
  country: "",
  state: "",
  adminName: "",
  email: "",
  role: "",
  phone: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "w-full h-11 px-4 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500";

const Field: React.FC<{ label: string; error?: string; help?: string; children: React.ReactNode }> = ({ label, error, help, children }) => (
  <label className="block">
    <span className="block mb-2 text-sm text-gray-700 dark:text-gray-300">{label}</span>
    {children}
    {error ? (
      <span className="block mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</span>
    ) : help ? (
      <span className="block mt-1.5 text-sm text-gray-600 dark:text-gray-400">{help}</span>
    ) : null}
  </label>
);

const Select: React.FC<{ value: string; onChange: (v: string) => void; options: string[]; placeholder: string; disabled?: boolean }> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`${inputCls} appearance-none pr-10 disabled:opacity-60 disabled:cursor-not-allowed ${value ? "" : "text-gray-400"}`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-700 dark:text-gray-300 pointer-events-none" />
  </div>
);

const Stepper: React.FC<{ current: number }> = ({ current }) => (
  <div className="flex items-center gap-3 overflow-x-auto">
    {STEPS.map((label, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <React.Fragment key={label}>
          {i > 0 && <div className={`flex-1 min-w-[24px] h-px ${done || active ? "bg-gray-800 dark:bg-gray-300" : "bg-gray-300 dark:bg-gray-700"}`} />}
          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`flex items-center justify-center w-6 h-6 text-xs font-medium border ${active ? "rounded-md" : "rounded-full"} ${
                done
                  ? "bg-emerald-300 border-emerald-600 text-emerald-900"
                  : active
                  ? "bg-emerald-100 border-emerald-600 text-emerald-900"
                  : "border-gray-300 dark:border-gray-600 text-gray-500"
              }`}
            >
              {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span className={`text-sm ${active || done ? "text-gray-900 dark:text-gray-100" : "text-gray-500"}`}>{label}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

const NewClientSetup: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<ClientForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof ClientForm, string>>>({});

  // GET /resellers/industries — the Industry dropdown. Falls back to the
  // built-in list only if the request fails.
  const token = localStorage.getItem("authToken");
  const { makeRequest: getIndustries } = useRequest("/resellers/industries", "GET", { Authorization: `Bearer ${token}` });
  const [industries, setIndustries] = useState<string[]>([]);
  useEffect(() => {
    getIndustries().then(([res, status]) => {
      const list = status >= 200 && status < 300 && Array.isArray(res?.industries) ? res.industries : null;
      setIndustries(list && list.length ? list : INDUSTRIES);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key: keyof ClientForm) => (value: string) => {
    setForm((f) => ({ ...f, [key]: value, ...(key === "country" ? { state: "" } : {}) }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateBasics = () => {
    const next: Partial<Record<keyof ClientForm, string>> = {};
    (Object.keys(EMPTY_FORM) as (keyof ClientForm)[]).forEach((k) => {
      if (!form[k].trim()) next[k] = "Required";
    });
    if (form.email && !EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid email address";
    if (form.phone && form.phone.replace(/\D/g, "").length < 7) next.phone = "Enter a valid phone number";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleNext = () => {
    if (step === 0 && !validateBasics()) return;
    if (step < STEPS.length - 1) setStep(step + 1);
  };

  const handleBack = () => (step === 0 ? navigate("/reseller/home") : setStep(step - 1));

  const summary: [string, string][] = [
    ["Company Name", form.companyName],
    ["Workspace", form.workspace],
    ["Industry", form.industry],
    ["Country", form.country],
    ["State", form.state],
    ["Admin Name", form.adminName],
    ["Email", form.email],
    ["Role", form.role],
    ["Phone", form.phone],
  ];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl min-h-full overflow-hidden">
      <div className="px-4 sm:px-6 pt-6 pb-5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/reseller/home")}
            title="Back to Home"
            className="flex items-center justify-center w-9 h-9 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p className="text-lg">
            <span className="text-gray-500">Home</span>
            <span className="mx-2 text-gray-800 dark:text-gray-300">/</span>
            <span className="text-gray-900 dark:text-gray-100">New Client Setup</span>
          </p>
        </div>
        <div className="mt-5">
          <Stepper current={step} />
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-950/40 px-4 sm:px-6 py-6 min-h-[60vh]">
        {step === 0 && (
          <>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Client Setup Details</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Enter the details of your client whom you would like to add</p>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="Company Name" error={errors.companyName}>
                <input className={inputCls} value={form.companyName} onChange={(e) => set("companyName")(e.target.value)} placeholder="e.g. Meridian Bank Nigeria" />
              </Field>
              <Field label="Workspace" error={errors.workspace}>
                <Select value={form.workspace} onChange={set("workspace")} options={INDUSTRIES} placeholder="Select workspace" />
              </Field>
              <Field label="Industry" error={errors.industry}>
                <Select
                  value={form.industry}
                  onChange={set("industry")}
                  options={industries}
                  placeholder={industries.length ? "Select industry" : "Loading industries…"}
                  disabled={!industries.length}
                />
              </Field>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Country" error={errors.country}>
                <Select value={form.country} onChange={set("country")} options={Object.keys(COUNTRY_STATES)} placeholder="Select country" />
              </Field>
              <Field label="State" error={errors.state}>
                <Select
                  value={form.state}
                  onChange={set("state")}
                  options={COUNTRY_STATES[form.country] || []}
                  placeholder={form.country ? "Select state" : "Select a country first"}
                  disabled={!form.country}
                />
              </Field>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Admin Name" error={errors.adminName}>
                <input className={inputCls} value={form.adminName} onChange={(e) => set("adminName")(e.target.value)} placeholder="e.g. John Doe" />
              </Field>
              <Field label="Email" error={errors.email} help="We will use this to send the client invite email">
                <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="admin@company.com" />
              </Field>
              <Field label="Role" error={errors.role}>
                <Select value={form.role} onChange={set("role")} options={CLIENT_ROLES} placeholder="Select role" />
              </Field>
              <Field label="Phone" error={errors.phone}>
                <input type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="+2348012345678" />
              </Field>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Licensing</h2>
            <div className="mt-6 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-sm text-gray-500 dark:text-gray-400">
              The licensing step is waiting on its design.
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Review and Invite</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Check the details before inviting {form.adminName || "the client admin"}.</p>
            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 sm:grid-cols-2 lg:grid-cols-3">
              {summary.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs text-gray-500 dark:text-gray-400">{k}</dt>
                  <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          </>
        )}

        <div className="mt-10 flex justify-end gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="h-11 px-3 rounded-md bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Back
          </button>
          {step < STEPS.length - 1 ? (
            <button type="button" onClick={handleNext} className="h-11 px-3 rounded-md bg-indigo-700 hover:bg-indigo-800 text-sm font-medium text-white">
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => toast.info("Client invites aren't connected yet — there's no reseller API.")}
              className="h-11 px-3 rounded-md bg-indigo-700 hover:bg-indigo-800 text-sm font-medium text-white"
            >
              Send Invite
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewClientSetup;
